/**
 * bossTurret.ts
 * Boss-projectile pool + turret-fire helper for the Void Mothership.
 * Sibling of `bossSystem.ts` — extracted to keep that module within the
 * 300-line file budget.
 *
 * Owns:
 *  - the `BossProjectile` pool (capped at
 *    `VOID_MOTHERSHIP_MAX_BOSS_PROJECTILES`, pre-warmed at module load),
 *  - `fireBossTurret()` — single-shot acquire/spawn,
 *  - `updateBossProjectiles()` — per-tick integrate + cull,
 *  - `releaseAllBossProjectiles()` — bulk release on scene teardown.
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * @module bossTurret
 */

import { spaceState } from './spaceState';
import type { LevelRuntime } from './gameState';
import { BossProjectile } from '../entities/BossProjectile';
import { ObjectPool } from '../utils/objectPool';
import {
  VOID_MOTHERSHIP_PROJECTILE_HEIGHT,
  VOID_MOTHERSHIP_PROJECTILE_WIDTH,
  VOID_MOTHERSHIP_MAX_BOSS_PROJECTILES,
} from '../../constants/bossConstants';

/** Reusable empty level for projectile update — space has no platforms. */
const EMPTY_SPACE_LEVEL: LevelRuntime = { width: 0, height: 0, platforms: [] };

/**
 * Module-level boss-projectile pool. Pre-warmed at module load so the
 * first turret shot doesn't pay an allocation cost. Sized to
 * `VOID_MOTHERSHIP_MAX_BOSS_PROJECTILES` — a finite ceiling above the
 * peak turret cadence, so `acquire()` returning `null` is a sign the
 * cadence was tuned too aggressively rather than a normal occurrence.
 */
const bossProjectilePool = new ObjectPool<BossProjectile>(
  () => new BossProjectile(),
  VOID_MOTHERSHIP_MAX_BOSS_PROJECTILES,
);
bossProjectilePool.prewarm(VOID_MOTHERSHIP_MAX_BOSS_PROJECTILES);

/**
 * Test-only helper: number of boss projectiles currently parked in the pool.
 *
 * @returns Count of available pooled instances.
 * @internal
 */
export function _bossProjectilePoolAvailable(): number {
  return bossProjectilePool.available;
}

/**
 * Spawn a single boss-turret projectile at the boss's centre-left edge.
 * Returns `true` when a bolt was spawned, `false` when the pool was
 * exhausted (extremely unlikely at greybox cadence; pool capacity is
 * sized well above peak demand). Callers use the return value to gate
 * the turret cooldown so a pool miss doesn't burn the shot — see
 * `updateBoss` and the matching DECISION in `tryFireShipProjectile`.
 *
 * @returns `true` when a bolt was spawned, `false` on a pool miss.
 */
export function fireBossTurret(): boolean {
  const boss = spaceState.boss;
  if (boss === null) return false;
  const projectile = bossProjectilePool.acquire();
  if (projectile === null) return false;
  // DECISION: Spawn so the bolt's RIGHT edge is flush with the boss's
  // LEFT edge — entity coords are top-left and the bolt travels left, so
  // `spawnX = boss.x` would clip a few frames of overlap inside the boss
  // hitbox before clearing. Mirrors the ship-side muzzle offset.
  const spawnX = boss.x - VOID_MOTHERSHIP_PROJECTILE_WIDTH;
  const spawnY = boss.y + boss.height / 2 - VOID_MOTHERSHIP_PROJECTILE_HEIGHT / 2;
  projectile.spawn(spawnX, spawnY);
  spaceState.bossProjectiles.push(projectile);
  return true;
}

/**
 * Update every active boss projectile and release dead ones back to the
 * pool. Mirrors `updateShipProjectiles` — separate from `updateBoss` so
 * the projectile stream keeps culling even after the boss dies (a kill
 * shouldn't visually "freeze" the in-flight bolts).
 *
 * @param dt - Fixed timestep in seconds.
 */
export function updateBossProjectiles(dt: number): void {
  const arr = spaceState.bossProjectiles;
  for (let i = arr.length - 1; i >= 0; i--) {
    const p = arr[i];
    p.update(dt, EMPTY_SPACE_LEVEL);
    if (p.dead) {
      bossProjectilePool.release(p);
      arr.splice(i, 1);
    }
  }
}

/**
 * Release every live boss projectile back to the pool and clear the
 * scene's bookkeeping list. Called from `resetBoss()` so a re-init of
 * the space level starts from a clean slate.
 */
export function releaseAllBossProjectiles(): void {
  for (let i = 0; i < spaceState.bossProjectiles.length; i++) {
    bossProjectilePool.release(spaceState.bossProjectiles[i]);
  }
  spaceState.bossProjectiles.length = 0;
}
